/**
 * Navigation Helper Utilities
 * 
 * Provides utility functions for managing URL-based navigation state
 * and return URL handling for invoice management workflow.
 */

import { INVOICE_STATUSES, InvoiceStatusKey } from '../constants/invoiceStatus';

export type StatusFilterType = InvoiceStatusKey | 'all';

/**
 * Type guard to check if a string is a valid invoice status
 * @param status - String to check
 * @returns True if the string is a valid invoice status
 */
const isValidInvoiceStatus = (status: string): status is InvoiceStatusKey => {
  return Object.values(INVOICE_STATUSES).includes(status as InvoiceStatusKey);
};

/**
 * Builds a return URL based on current search parameters
 * @param searchParams - Current URL search parameters
 * @returns Return URL string
 */
export const buildReturnUrl = (searchParams: URLSearchParams): string => {
  const status = searchParams.get('status');
  const search = searchParams.get('search');
  const supplier = searchParams.get('supplier');
  const storeId = searchParams.get('storeId');
  const dateStart = searchParams.get('dateStart');
  const dateEnd = searchParams.get('dateEnd');
  const page = searchParams.get('page');
  const assignedTo = searchParams.get('assignedTo');
  
  // Build query parameters array
  const params: string[] = [];
  
  if (status) {
    params.push(`status=${encodeURIComponent(status)}`);
  }
  
  if (assignedTo) {
    params.push(`assignedTo=${encodeURIComponent(assignedTo)}`);
  }
  
  if (search) {
    params.push(`search=${encodeURIComponent(search)}`);
  }

  if (supplier) {
    params.push(`supplier=${encodeURIComponent(supplier)}`);
  }

  if (storeId) {
    params.push(`storeId=${encodeURIComponent(storeId)}`);
  }

  if (dateStart) {
    params.push(`dateStart=${encodeURIComponent(dateStart)}`);
  }

  if (dateEnd) {
    params.push(`dateEnd=${encodeURIComponent(dateEnd)}`);
  }
  
  if (page) {
    params.push(`page=${encodeURIComponent(page)}`);
  }
  
  // Return URL with preserved parameters
  return params.length > 0 ? `/invoices?${params.join('&')}` : '/invoices';
};

/**
 * Creates an edit URL with return parameter
 * @param invoiceId - Invoice ID
 * @param returnUrl - URL to return to after editing
 * @returns Edit URL with return parameter
 */
export const getEditUrlWithReturn = (invoiceId: string, returnUrl: string): string => {
  return `/invoices/${invoiceId}/edit?returnTo=${encodeURIComponent(returnUrl)}`;
};

/**
 * Validates URL status parameter against user permissions
 * @param status - Status from URL parameter
 * @param userRoles - User roles array
 * @param defaultStatus - Default status to fallback to
 * @returns Valid status for the user (guaranteed to be a valid StatusFilterType)
 */
export const validateUrlStatus = (
  status: string | null, 
  userRoles: { name: string }[] | undefined,
  defaultStatus: StatusFilterType
): StatusFilterType => {
  // Return default if no status provided
  if (!status) return defaultStatus;
  
  // Return default if status is not a valid invoice status
  if (!isValidInvoiceStatus(status)) return defaultStatus;
  
  // Define allowed statuses for different user types
  const stockAllowedStatuses: InvoiceStatusKey[] = [
    INVOICE_STATUSES.ADDING, 
    INVOICE_STATUSES.NEW_PRODUCTS, 
    INVOICE_STATUSES.IR, 
    INVOICE_STATUSES.INVOICING, 
    INVOICE_STATUSES.ENTERING_STOCK, 
    INVOICE_STATUSES.COMPLETED
  ];
  const adminFinanceAllowedStatuses: InvoiceStatusKey[] = [
    INVOICE_STATUSES.UNPROCESSED, 
    INVOICE_STATUSES.ADDING, 
    INVOICE_STATUSES.NEW_PRODUCTS, 
    INVOICE_STATUSES.IR, 
    INVOICE_STATUSES.INVOICING, 
    INVOICE_STATUSES.HEAD_OFFICE_STOCK,
    INVOICE_STATUSES.EXPENSE_INVOICES,
    INVOICE_STATUSES.ENTERING_STOCK, 
    INVOICE_STATUSES.ENTERING_EXPENSE,
    INVOICE_STATUSES.CREDIT_NOTE,
    INVOICE_STATUSES.CREDIT_ENTERING,
    INVOICE_STATUSES.COMPLETED
  ];
  
  // Determine user type
  const hasStock = userRoles?.some((role) => role.name === 'stock') ?? false;
  const hasAdminOrFinance = userRoles?.some((role) => role.name === 'admin' || role.name === 'finance') ?? false;
  
  // Check if status is allowed for this user type
  if (hasStock && !hasAdminOrFinance) {
    return stockAllowedStatuses.includes(status) ? status : defaultStatus;
  }
  
  return adminFinanceAllowedStatuses.includes(status) ? status : defaultStatus;
};

/**
 * Gets the default status for a user based on their roles
 * @param userRoles - User roles array
 * @returns Default status for the user
 */
export const getDefaultStatusForUser = (userRoles: { name: string }[] | undefined): StatusFilterType => {
  const hasStock = userRoles?.some((role) => role.name === 'stock') ?? false;
  const hasAdminOrFinance = userRoles?.some((role) => role.name === 'admin' || role.name === 'finance') ?? false;
  
  if (hasStock && !hasAdminOrFinance) {
    // Stock users can only see specific statuses, default to first allowed one
    return INVOICE_STATUSES.ADDING;
  }
  
  // Admin and finance users can see 'unprocessed'
  return INVOICE_STATUSES.UNPROCESSED;
};

/**
 * Extracts return URL from search parameters
 * @param searchParams - Current URL search parameters
 * @param fallbackUrl - Fallback URL if no return URL is found
 * @returns Decoded return URL or fallback
 */
export const getReturnUrl = (searchParams: URLSearchParams, fallbackUrl: string = '/invoices'): string => {
  const returnTo = searchParams.get('returnTo');
  return returnTo ? decodeURIComponent(returnTo) : fallbackUrl;
};
